import { useLingui } from "@lingui/react/macro";
import { ModelData, calculateDurationH } from "./modelDetails.lib";
import { niceNumber } from "@/lib/util";

const COLOR_MODEL = "var(--secondary-purple-500)";
const COLOR_NORMATIVE = "var(--secondary-green-500)";
const COLOR_AXIS = "#ccc";
const COLOR_TICK = "#999";
const COLOR_LABEL = "#555";

type Props = {
    model: ModelData;
};

/**
 * Picks a "nice" step size near the requested range (1/2/5 × 10^n).
 * Classic Wilkinson-style helper for readable axis ticks.
 */
function niceStep(range: number, round: boolean): number {
    if (range <= 0 || !Number.isFinite(range)) {
        return 1;
    }
    const exponent = Math.floor(Math.log10(range));
    const fraction = range / 10 ** exponent;
    let nice_fraction: number;
    if (round) {
        if (fraction < 1.5) {
            nice_fraction = 1;
        } else if (fraction < 3) {
            nice_fraction = 2;
        } else if (fraction < 7) {
            nice_fraction = 5;
        } else {
            nice_fraction = 10;
        }
    } else if (fraction <= 1) {
        nice_fraction = 1;
    } else if (fraction <= 2) {
        nice_fraction = 2;
    } else if (fraction <= 5) {
        nice_fraction = 5;
    } else {
        nice_fraction = 10;
    }
    return nice_fraction * 10 ** exponent;
}

/**
 * Builds ~target_tick_count evenly spaced nice ticks from 0 to a nice max covering `raw_max`.
 */
function buildAxisTicks(raw_max: number, target_tick_count = 5): { ticks: number[]; nice_max: number } {
    if (raw_max <= 0 || !Number.isFinite(raw_max)) {
        return { ticks: [0], nice_max: 1 };
    }
    const nice_range = niceStep(raw_max, false);
    const step = niceStep(nice_range / Math.max(target_tick_count - 1, 1), true);
    const nice_max = Math.ceil(raw_max / step) * step;
    const ticks: number[] = [];
    for (let value = 0; value <= nice_max + step * 1e-9; value += step) {
        // Avoid floating-point drift in labels (e.g. 0.30000000004)
        ticks.push(Number(value.toPrecision(12)));
    }
    return { ticks, nice_max };
}

function formatTickLabel(value: number): string {
    if (value === 0) {
        return "0";
    }
    if (Math.abs(value) >= 1000) {
        return niceNumber(value, 0);
    }
    if (Math.abs(value) >= 10) {
        return niceNumber(value, 1);
    }
    return niceNumber(value, 2);
}

/**
 * Renders a simple scatter plot with a linear regression line for the model and normative data.
 */
const HistoricalDataGraph = ({ model }: Props) => {
    const { t } = useLingui();

    if (!model.historicalData || model.historicalData.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500">{t`No historical data available for visualization.`}</div>
        );
    }

    const allQuantities = model.historicalData.map(d => d.quantity);
    const allDurations = model.historicalData.map(d => d.duration);
    const data_max_quantity = Math.max(...allQuantities, 0);
    const data_max_duration = Math.max(...allDurations, 0);

    // Include regression line endpoints so axes cover plotted lines as well as points
    const model_duration_at_data_max = calculateDurationH(data_max_quantity, model.modelSetup, model.modelSpeed);
    const normative_duration_at_data_max = calculateDurationH(
        data_max_quantity,
        model.normativeSetup,
        model.normativeSpeed
    );
    const raw_max_quantity = data_max_quantity * 1.05;
    const raw_max_duration =
        Math.max(
            data_max_duration,
            model_duration_at_data_max,
            normative_duration_at_data_max,
            model.modelSetup,
            model.normativeSetup
        ) * 1.05;

    const { ticks: x_ticks, nice_max: maxQuantity } = buildAxisTicks(raw_max_quantity, 6);
    const { ticks: y_ticks, nice_max: maxDuration } = buildAxisTicks(raw_max_duration, 5);

    const modelDurationHEnd = calculateDurationH(maxQuantity, model.modelSetup, model.modelSpeed);
    const normativeDurationHEnd = calculateDurationH(maxQuantity, model.normativeSetup, model.normativeSpeed);

    const chartHeight = 280;
    const chartWidth = 720;
    const padding_left = 56;
    const padding_right = 24;
    const padding_top = 24;
    const padding_bottom = 48;
    const plot_width = chartWidth - padding_left - padding_right;
    const plot_height = chartHeight - padding_top - padding_bottom;
    const tick_length = 5;

    const scaleX = (q: number) => (q / maxQuantity) * plot_width + padding_left;
    const scaleY = (d: number) => chartHeight - padding_bottom - (d / maxDuration) * plot_height;

    const axis_y = chartHeight - padding_bottom;
    const axis_x = padding_left;

    const points = model.historicalData.map(d => ({
        cx: scaleX(d.quantity) || 0,
        cy: scaleY(d.duration) || 0,
    }));

    return (
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width="100%" height="100%" className="chart-svg">
            {/* Axes */}
            <line x1={axis_x} y1={axis_y} x2={chartWidth - padding_right} y2={axis_y} stroke={COLOR_AXIS} />
            <line x1={axis_x} y1={padding_top} x2={axis_x} y2={axis_y} stroke={COLOR_AXIS} />

            {/* X-axis ticks */}
            {x_ticks.map(tick => {
                const x = scaleX(tick);
                return (
                    <g key={`x-${tick}`}>
                        <line x1={x} y1={axis_y} x2={x} y2={axis_y + tick_length} stroke={COLOR_TICK} />
                        <line
                            x1={x}
                            y1={padding_top}
                            x2={x}
                            y2={axis_y}
                            stroke={COLOR_AXIS}
                            strokeWidth="1"
                            opacity="0.35"
                        />
                        <text x={x} y={axis_y + 18} textAnchor="middle" fontSize="10" fill={COLOR_LABEL}>
                            {formatTickLabel(tick)}
                        </text>
                    </g>
                );
            })}

            {/* Y-axis ticks */}
            {y_ticks.map(tick => {
                const y = scaleY(tick);
                return (
                    <g key={`y-${tick}`}>
                        <line x1={axis_x - tick_length} y1={y} x2={axis_x} y2={y} stroke={COLOR_TICK} />
                        <line
                            x1={axis_x}
                            y1={y}
                            x2={chartWidth - padding_right}
                            y2={y}
                            stroke={COLOR_AXIS}
                            strokeWidth="1"
                            opacity="0.35"
                        />
                        <text x={axis_x - tick_length - 4} y={y + 3} textAnchor="end" fontSize="10" fill={COLOR_LABEL}>
                            {formatTickLabel(tick)}
                        </text>
                    </g>
                );
            })}

            {/* X-Axis Label */}
            <text
                x={(padding_left + chartWidth - padding_right) / 2}
                y={chartHeight - 8}
                textAnchor="middle"
                fontSize="12"
                fill={COLOR_LABEL}
            >
                {t`Quantity (Parts)`}
            </text>
            {/* Y-Axis Label */}
            <text
                x={14}
                y={chartHeight / 2}
                textAnchor="middle"
                fontSize="12"
                fill={COLOR_LABEL}
                transform={`rotate(-90, 14, ${chartHeight / 2})`}
            >
                {t`Duration (h)`}
            </text>

            {/* Model Regression Line */}
            <line
                x1={scaleX(0)}
                y1={scaleY(model.modelSetup)}
                x2={scaleX(maxQuantity)}
                y2={scaleY(modelDurationHEnd)}
                stroke={COLOR_MODEL}
                strokeWidth="2"
                strokeDasharray="4 4"
            />

            {/* Normative Regression Line */}
            <line
                x1={scaleX(0)}
                y1={scaleY(model.normativeSetup)}
                x2={scaleX(maxQuantity)}
                y2={scaleY(normativeDurationHEnd)}
                stroke={COLOR_NORMATIVE}
                strokeWidth="2"
                opacity="0.7"
            />

            {/* Data Points */}
            {points.map((p, index) => (
                <circle key={index} cx={p.cx} cy={p.cy} r="4" fill={COLOR_MODEL} opacity="0.7">
                    <title>{`${t`Qty`}: ${model.historicalData[index].quantity}, ${t`Duration`}: ${model.historicalData[index].duration} ${t`h`}`}</title>
                </circle>
            ))}

            {/* Legend */}
            <g transform={`translate(${padding_left + 15}, ${padding_top + 4})`} fontSize="12">
                <circle cx="5" cy="5" r="4" fill={COLOR_MODEL} />
                <text x="15" y="8" fill={COLOR_LABEL}>
                    {t`Historical Data`}
                </text>

                <line x1="0" y1="25" x2="10" y2="25" stroke={COLOR_MODEL} strokeWidth="2" strokeDasharray="4 4" />
                <text x="15" y="28" fill={COLOR_LABEL}>
                    {t`Model Prediction`}
                </text>

                <line x1="0" y1="45" x2="10" y2="45" stroke={COLOR_NORMATIVE} strokeWidth="2" opacity="0.7" />
                <text x="15" y="48" fill={COLOR_LABEL}>
                    {t`Normative Baseline`}
                </text>
            </g>
        </svg>
    );
};

export default HistoricalDataGraph;
